import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ClipboardList,
  Copy,
  MessageSquare,
  PartyPopper,
  Plus,
  Search,
  Users,
} from "lucide-react";
import { formApi } from "./api";
import type { FeedbackForm } from "./contract";
import { template, TEMPLATES } from "./templates";
import { useDebounce } from "../../hooks/useDebounce";
export function FormsList({
  canWrite,
  eventId,
}: {
  canWrite: boolean;
  eventId?: string;
}) {
  const navigate = useNavigate(),
    [forms, setForms] = useState<FeedbackForm[]>([]),
    [search, setSearch] = useState(""),
    [page, setPage] = useState(0),
    [total, setTotal] = useState(0),
    [status, setStatus] = useState("all"),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(true),
    [busy, setBusy] = useState(false);
  const query = useDebounce(search, 250);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    formApi
      .list(query, page, eventId, status)
      .then((result) => {
        if (!cancelled) {
          setForms(result.forms);
          setTotal(result.total);
          setError("");
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query, page, eventId, status]);
  async function create(kind: string, source?: FeedbackForm) {
    setBusy(true);
    setError("");
    try {
      const form = await formApi.create(
        source
          ? {
              ...source,
              title: `${source.title} (copy)`,
              status: "draft",
              closes_at: null,
            }
          : template(kind, eventId),
      );
      navigate(`/forms/${form.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create form.");
    } finally {
      setBusy(false);
    }
  }
  const icons = {
    blank: Plus,
    event: PartyPopper,
    community: MessageSquare,
    interest: Users,
  };
  return (
    <div className="space-y-6" data-tour="forms-library">
      {canWrite && (
        <section data-tour="forms-start">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium">Start with a good question</h2>
            <span className="text-xs text-glass-400">
              Make it yours in minutes
            </span>
          </div>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {TEMPLATES.map((t) => {
              const Icon = icons[t.icon];
              return (
                <button
                  type="button"
                  className="glass-card glass-card-interactive p-5 text-left min-h-[150px] disabled:opacity-50"
                  disabled={busy}
                  onClick={() => void create(t.id)}
                  key={t.id}
                >
                  <Icon className="w-5 h-5 text-accent-300 mb-5" />
                  <h3 className="font-medium text-sm">{t.title}</h3>
                  <p className="text-xs text-glass-400 mt-2 leading-relaxed">
                    {t.description}
                  </p>
                </button>
              );
            })}
          </div>
        </section>
      )}
      <section className="glass-card p-4 md:p-5">
        <div className="flex flex-wrap gap-3 items-center justify-between mb-5">
          <div>
            <h2 className="font-medium">
              {eventId ? "Forms for this event" : "Your forms"}
            </h2>
            <p className="text-xs text-glass-400 mt-1">
              Draft, share, listen, improve.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-glass-400" />
              <input
                aria-label="Search forms"
                className="glass-input pl-9"
                placeholder="Find a form…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </label>
            <select
              aria-label="Filter form status"
              className="glass-select"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(0);
              }}
            >
              <option value="all">All statuses</option>
              <option value="draft">Drafts</option>
              <option value="published">Published</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        </div>
        {error && (
          <p role="alert" className="text-red-400 text-sm mb-4">
            {error}
          </p>
        )}
        {loading ? (
          <p role="status" className="text-sm text-glass-400 py-10 text-center">
            Loading forms…
          </p>
        ) : (
          <div className="space-y-2">
            {forms
              .filter((f) => status === "all" || f.status === status)
              .map((form) => (
                <div
                  key={form.id}
                  className="subcard p-4 flex flex-wrap gap-3 items-center"
                >
                  <ClipboardList className="w-5 h-5 text-glass-400 shrink-0" />
                  <Link
                    to={`/forms/${form.id}`}
                    className="min-w-0 flex-1 group"
                  >
                    <div className="flex gap-2 flex-wrap items-center">
                      <h3 className="font-medium text-sm group-hover:text-accent-300 truncate">
                        {form.title}
                      </h3>
                      <span className="badge text-[10px] capitalize">
                        {form.status}
                      </span>
                    </div>
                    <p className="text-xs text-glass-400 mt-1">
                      {form.event_title ? `${form.event_title} · ` : ""}
                      {form.response_count} response
                      {form.response_count === 1 ? "" : "s"} · Updated{" "}
                      {new Date(form.updated_at).toLocaleDateString()}
                    </p>
                  </Link>
                  {canWrite && (
                    <button
                      type="button"
                      className="btn-ghost min-h-[44px]"
                      title="Duplicate form"
                      aria-label={`Duplicate ${form.title}`}
                      disabled={busy}
                      onClick={() => void create("", form)}
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  )}
                  {canWrite && (
                    <DeleteDraftButton
                      form={form}
                      disabled={busy}
                      onDeleted={() => {
                        setForms((previous) =>
                          previous.filter((f) => f.id !== form.id),
                        );
                        setTotal((previous) => Math.max(0, previous - 1));
                      }}
                    />
                  )}
                  <Link
                    className="btn-ghost min-h-[44px]"
                    aria-label={`Open ${form.title}`}
                    to={`/forms/${form.id}`}
                  >
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              ))}
            {forms.filter((f) => status === "all" || f.status === status)
              .length === 0 && (
              <div className="py-12 text-center">
                <ClipboardList className="w-8 h-8 text-glass-400 mx-auto mb-4" />
                <h3 className="text-sm font-medium">
                  {search ? "No matching forms" : "A fresh start"}
                </h3>
                <p className="text-xs text-glass-400 mt-2">
                  {search
                    ? "Try another title."
                    : "Choose a template above to start listening to your community."}
                </p>
              </div>
            )}
          </div>
        )}
        {total > 50 && (
          <div className="flex justify-end gap-3 mt-4 items-center text-xs">
            <button
              className="glass-button-secondary"
              disabled={!page}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <span>
              Page {page + 1} of {Math.ceil(total / 50)}
            </span>
            <button
              className="glass-button-secondary"
              disabled={(page + 1) * 50 >= total}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
import { DeleteDraftButton } from "./DeleteDraftButton";
